import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BODY_FONT, g, t, TICKET } from '../../HomeScreen/HomeScreen.styles';
import { useTrackEvent } from '../../../../common/hooks/useTrackEvent';
import { EventNames } from '../../../../common/constants/constants';
import { useTypedSelector } from '../../../../store/store';

const BlockTitle = styled('h2')({
  fontSize: t(30),
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  fontWeight: 700,
  fontFamily: BODY_FONT,
  color: TICKET.labelInk,
  margin: `0 0 ${g(26)}`,
  textAlign: 'left',
});

const FieldRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: g(14),
  marginBottom: g(14),
});

const Input = styled('input')(({ theme }) => ({
  width: '100%',
  height: `max(44px, ${g(76)})`,
  borderRadius: g(10),
  border: '1px solid #c9c8c4',
  background: '#fbfaf8',
  padding: `0 ${g(20)}`,
  // 16px floor: iOS zooms the page into any smaller field on focus.
  fontSize: '16px',
  fontFamily: BODY_FONT,
  color: TICKET.labelInk,
  outline: 'none',
  transition: 'border-color 150ms ease, box-shadow 150ms ease',
  '&::placeholder': { color: '#a4a5a7' },
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`,
  },
  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
    WebkitTextFillColor: TICKET.labelInk,
    WebkitBoxShadow: '0 0 0 1000px #fbfaf8 inset',
    transition: 'background-color 5000s ease-in-out 0s',
  },
}));

const CtaButton = styled('button')(({ theme, disabled }) => ({
  width: '100%',
  height: `max(50px, ${g(90)})`,
  borderRadius: g(14),
  border: 'none',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: t(30),
  fontWeight: 700,
  fontFamily: BODY_FONT,
  letterSpacing: '-0.01em',
  boxShadow: '0 1.5px 2px rgba(0,0,0,.24), 0 8px 18px rgba(0,0,0,.08)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  marginTop: g(14),
  opacity: disabled ? 0.45 : 1,
  transition: 'opacity 160ms ease, transform 160ms ease',
  '&:active': disabled ? {} : { transform: 'scale(0.985)' },
}));

interface Props {
  onConfirm: (data: { firstName: string; lastName: string; phone: string }) => void;
  initialFirstName?: string;
  initialLastName?: string;
  isSubmitting?: boolean;
  /** True when this block is the visible step. Defaults to true (overlay use). */
  isActive?: boolean;
}

const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const phoneDigits = (s: string) => s.replace(/\D/g, '').slice(0, 10);

export const IdentityBlock: FC<Props> = ({
  onConfirm,
  initialFirstName = '',
  initialLastName = '',
  isSubmitting,
  isActive = true,
}) => {
  const { trackEvent } = useTrackEvent();
  const {
    account: { id: accountId },
  } = useTypedSelector((state) => state.account);
  const { id: userId } = useTypedSelector((state) => state.me);

  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [phone, setPhone] = useState('');

  const isValid = firstName.trim().length >= 1 && lastName.trim().length >= 1 && phoneDigits(phone).length === 10;

  const uploadPct = useTypedSelector((state) => state.upload.uploadingProgress);
  const uploadPctRef = useRef(uploadPct);
  uploadPctRef.current = uploadPct;
  const mountedAtRef = useRef(performance.now());

  // View events fire when the block is actually the visible step, not on
  // mount: the carousel renders every slide up front, so a mount-time event
  // fired for users who never saw the form.
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (!isActive || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackEvent({
      action: EventNames.Phone_Verification_Page,
      location: window.location.href,
      accountId,
      userId,
    });
    trackEvent({
      action: EventNames.identity_block_view,
      upload_pct_at_view: uploadPctRef.current,
      prefilled_fields: [initialFirstName ? 'first_name' : '', initialLastName ? 'last_name' : '']
        .filter(Boolean)
        .join(','),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  }, []);

  // Field names only — never the typed value (PII stays in the browser).
  const fieldKey = (name: string): 'first_name' | 'last_name' | 'phone' =>
    name === 'firstName' ? 'first_name' : name === 'lastName' ? 'last_name' : 'phone';

  const handleFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    trackEvent({ action: EventNames.identity_field_focus, field: fieldKey(e.target.name) });
  };

  const handlePhoneBlur = () => {
    const digits = phoneDigits(phone);
    trackEvent({ action: EventNames.Signup_Phone_Typed, form: 'identity_block' });
    trackEvent({ action: EventNames.input_field_focus, name: 'phoneNumber' });
    trackEvent({
      action: EventNames.identity_field_filled,
      field: 'phone',
      valid: digits.length === 10,
      length: digits.length,
      phone_country: 'US',
    });
  };

  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const key = e.target.name || e.target.placeholder?.toLowerCase();
    trackEvent({ action: EventNames.input_field_focus, name: key });
    trackEvent({
      action: EventNames.identity_field_filled,
      field: fieldKey(e.target.name),
      valid: e.target.value.trim().length >= 1,
      length: e.target.value.trim().length,
    });
  };

  const handleSubmit = () => {
    if (!isValid || isSubmitting) return;
    trackEvent({ action: EventNames.next_button_click });
    const filled = [firstName.trim() && 'first_name', lastName.trim() && 'last_name', phoneDigits(phone) && 'phone']
      .filter(Boolean)
      .join(',');
    trackEvent({
      action: EventNames.identity_submit,
      fields_filled: filled,
      phone_provided: phoneDigits(phone).length === 10,
      ms_on_block: Math.round(performance.now() - mountedAtRef.current),
      upload_pct_at_submit: uploadPctRef.current,
    });
    onConfirm({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phoneDigits(phone) });
  };

  return (
    <section aria-label="Confirm identity">
      <BlockTitle>We want to confirm it&apos;s you.</BlockTitle>

      <FieldRow>
        <div>
          <Input
            type="text"
            name="firstName"
            placeholder="First"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={handleFieldBlur}
            onFocus={handleFieldFocus}
          />
        </div>
        <div>
          <Input
            type="text"
            name="lastName"
            placeholder="Last"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={handleFieldBlur}
            onFocus={handleFieldFocus}
          />
        </div>
      </FieldRow>

      <div>
        <Input
          type="tel"
          inputMode="tel"
          name="phoneNumber"
          placeholder="(555) 555-5555"
          autoComplete="tel"
          value={phone}
          onChange={handlePhoneChange}
          onBlur={handlePhoneBlur}
          onFocus={handleFieldFocus}
        />
      </div>

      <CtaButton disabled={!isValid || isSubmitting} onClick={handleSubmit}>
        Yes, It&apos;s Me!
      </CtaButton>
    </section>
  );
};
